'use client'

import { useApolloClient, useMutation } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import type { Product } from '@gql-generated/graphql'
import {
  CreateFavoritesListForProductDocument,
  UpdateFavoritesListForProductDocument,
  WishlistOrigin,
} from '@gql-generated/graphql'
import type { GraphQLFormattedError } from 'graphql'
import { useCallback } from 'react'

gql(/* GraphQL */ `
  mutation updateFavoritesListForProduct($input: WishlistUpdateInput_V2!) {
    updateWishlist_V2(input: $input) {
      id
    }
  }
`)

gql(/* GraphQL */ `
  mutation createFavoritesListForProduct($input: WishlistCreateInput_V2!) {
    createWishlist_V2(input: $input) {
      id
      name
    }
  }
`)

type UseAddProductToFavoriteListParams = {
  name?: string // will force creation if present
  favoriteListId?: number // will be ignored if name is present
  productRef?: string
}

type UseAddProductToFavoriteListReturnType = () => Promise<{
  errors?: readonly GraphQLFormattedError[] | undefined
}>

export function useAddProductToFavoriteList({
  productRef,
  favoriteListId,
  name,
}: UseAddProductToFavoriteListParams): UseAddProductToFavoriteListReturnType {
  const [createFavoritesListMutation] = useMutation(CreateFavoritesListForProductDocument)
  const [updateFavoritesListMutation] = useMutation(UpdateFavoritesListForProductDocument)

  const client = useApolloClient()

  const updateFavoriteList = useCallback(async () => {
    // 1 - Check we have a valid product reference

    if (!productRef) {
      console.error('Product reference is required to add it to a favorite list')

      return {
        errors: [
          {
            message: 'Product reference is required to add it to a favorite list',
          } satisfies GraphQLFormattedError,
        ],
      }
    }

    // 2 - Update or create favorite list and add product to it

    if (name) {
      const { errors } = await createFavoritesListMutation({
        variables: {
          input: {
            from: WishlistOrigin.Wishlist,
            name,
            products: [{ reference: productRef }],
          },
        },
      })

      if (errors && errors.length > 0) {
        return { errors }
      }
    } else if (typeof favoriteListId === 'number' && Number.isFinite(favoriteListId)) {
      const { errors } = await updateFavoritesListMutation({
        variables: {
          input: {
            id: favoriteListId,
            products: [{ reference: productRef }],
          },
        },
      })

      if (errors && errors.length > 0) {
        return { errors }
      }
    } else {
      console.error(
        'Either name or favoriteListId must be provided to create or update a favorite list',
      )

      return {
        errors: [
          {
            message:
              'Either name or favoriteListId must be provided to create or update a favorite list',
          } satisfies GraphQLFormattedError,
        ],
      }
    }

    // 3 - Update product's isFavorite field in Apollo's cache (because mutation result does not return product)

    const entity = client.cache.identify({
      __typename: 'Product',
      reference: productRef,
    } satisfies Partial<Product>)

    client.cache.modify({
      id: entity,
      fields: {
        isFavorite() {
          return true
        },
      } satisfies ApolloModifier<Product>,
    })

    return { errors: undefined }
  }, [
    productRef,
    name,
    favoriteListId,
    createFavoritesListMutation,
    client.cache,
    updateFavoritesListMutation,
  ])

  return updateFavoriteList
}
